import { useState } from "react";
import type { WorkflowNode } from "@/hooks/use-workflows";
import { NODE_TYPES } from "./node-palette";

const AD_TRIGGER_PRESETS = [
  { value: "ad.lead_form.submitted", label: "Ad Lead Form Submitted" },
  { value: "ad.conversion.tracked", label: "Ad Conversion Tracked" },
  { value: "ad.budget.exhausted", label: "Ad Budget Exhausted" },
  { value: "ad.performance.anomaly", label: "Ad Performance Anomaly" },
] as const;

interface NodeConfigProps {
  node: WorkflowNode;
  onUpdate: (config: Record<string, unknown>) => void;
  onDelete: () => void;
  onClose: () => void;
}

/** Per-node configuration panel — slides in from right when a node is selected. */
export function NodeConfig({ node, onUpdate, onDelete, onClose }: NodeConfigProps) {
  const nodeType = NODE_TYPES.find((n) => n.type === node.type);
  const [config, setConfig] = useState<Record<string, unknown>>(node.config);

  const updateField = (key: string, value: unknown) => {
    const updated = { ...config, [key]: value };
    setConfig(updated);
    onUpdate(updated);
  };

  return (
    <div className="w-72 shrink-0 overflow-y-auto border-l border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">{nodeType?.icon} {nodeType?.label ?? node.type}</p>
          <p className="text-xs text-muted-foreground">{nodeType?.description}</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
      </div>

      <div className="space-y-3">
        {/* Common: label */}
        <Field label="Label" value={String(config.label ?? "")}
          onChange={(v) => updateField("label", v)} />

        {/* Type-specific fields */}
        {node.type === "trigger_event" && (
          <>
            <SelectField
              label="Preset"
              value={String(config.eventType ?? "")}
              onChange={(value) => {
                if (value) {
                  updateField("eventType", value);
                }
              }}
              placeholder="Choose a common ad trigger"
              options={AD_TRIGGER_PRESETS.map((preset) => ({
                value: preset.value,
                label: preset.label,
              }))}
            />
            <Field label="Event Type" value={String(config.eventType ?? "")}
              onChange={(v) => updateField("eventType", v)}
              placeholder="crm.deal.stage_changed" />
            <p className="text-[11px] text-muted-foreground">
              Ads workflow presets map to the mounted ad events: lead form submitted, conversion tracked,
              budget exhausted, and performance anomaly.
            </p>
          </>
        )}

        {node.type === "trigger_form" && (
          <Field label="Form ID" value={String(config.formId ?? "")}
            onChange={(v) => updateField("formId", v)}
            placeholder="form-123" />
        )}

        {node.type === "trigger_scheduled" && (
          <Field label="Cron Expression" value={String(config.cron ?? "")}
            onChange={(v) => updateField("cron", v)}
            placeholder="0 9 * * 1" />
        )}

        {node.type === "condition_wait" && (
          <>
            <Field label="Duration (hours)" value={String(config.durationHours ?? "")}
              onChange={(v) => updateField("durationHours", Number(v))} type="number" />
            <Field label="Or Until Event" value={String(config.untilEvent ?? "")}
              onChange={(v) => updateField("untilEvent", v)}
              placeholder="email.opened" />
          </>
        )}

        {node.type === "condition_if" && (
          <>
            <Field label="Field" value={String(config.field ?? "")}
              onChange={(v) => updateField("field", v)}
              placeholder="lifecycleStage" />
            <Field label="Operator" value={String(config.operator ?? "equals")}
              onChange={(v) => updateField("operator", v)} />
            <Field label="Value" value={String(config.value ?? "")}
              onChange={(v) => updateField("value", v)} />
          </>
        )}

        {node.type === "condition_split" && (
          <Field label="Split % (A)" value={String(config.splitPercent ?? 50)}
            onChange={(v) => updateField("splitPercent", Number(v))} type="number" />
        )}

        {node.type === "action_send_email" && (
          <>
            <Field label="Template ID" value={String(config.templateId ?? "")}
              onChange={(v) => updateField("templateId", v)} />
            <Field label="Subject Override" value={String(config.subject ?? "")}
              onChange={(v) => updateField("subject", v)} />
          </>
        )}

        {node.type === "action_send_sms" && (
          <Field label="Message" value={String(config.message ?? "")}
            onChange={(v) => updateField("message", v)} multiline />
        )}

        {node.type === "action_webhook" && (
          <Field label="URL" value={String(config.url ?? "")}
            onChange={(v) => updateField("url", v)}
            placeholder="https://..." />
        )}

        {node.type === "action_update_crm" && (
          <>
            <Field label="Field" value={String(config.field ?? "")}
              onChange={(v) => updateField("field", v)} />
            <Field label="Value" value={String(config.value ?? "")}
              onChange={(v) => updateField("value", v)} />
          </>
        )}

        {node.type === "action_add_tag" && (
          <Field label="Tag" value={String(config.tag ?? "")}
            onChange={(v) => updateField("tag", v)} />
        )}
      </div>

      <div className="mt-6 border-t border-border pt-4">
        <button onClick={onDelete}
          className="w-full rounded-md border border-destructive/30 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/5">
          Delete Node
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type, multiline }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; multiline?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={3}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
      ) : (
        <input type={type ?? "text"} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <select
        aria-label={label}
        value={options.some((option) => option.value === value) ? value : ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
